import { useCallback, useState, useEffect, useRef } from 'react';
import { supabase } from '@/lib/supabase';

export interface TrainerMedia {
  id: string;
  name: string;
  path: string;
  publicUrl: string;
  createdAt: string;
  type: 'image' | 'video';
  thumbnail?: string;
  caption?: string;
  isFeatured?: boolean;
}

/**
 * Pass in the trainer's **Auth UID** (not users.id).
 */
export function useTrainerMedia(trainerAuthUid: string | undefined) {
  const [files, setFiles] = useState<TrainerMedia[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const filesRef = useRef<TrainerMedia[]>([]);

  // Keep filesRef in sync with files state
  useEffect(() => {
    filesRef.current = files;
  }, [files]);

  /* -------------------------------------------------- */
  /* List                                               */
  /* -------------------------------------------------- */
  const refresh = useCallback(async () => {
    if (!trainerAuthUid) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { data, error } = await supabase.storage
        .from('trainer_media')
        .list(`user_directory/${trainerAuthUid}`, {
          limit: 100,
          sortBy: { column: 'created_at', order: 'desc' }
        });

      if (error) throw error;

      // Get all metadata for this user's files in one query
      const { data: metadataRows, error: metadataError } = await supabase
        .from('trainer_media_metadata')
        .select('file_path, is_featured')
        .in('file_path', data.map(obj => `user_directory/${trainerAuthUid}/${obj.name}`));
      
      if (metadataError) {
        console.warn('Error fetching metadata:', metadataError);
      }
      
      // Create a map for quick lookups
      const metadataMap = new Map();
      if (metadataRows) {
        metadataRows.forEach(row => {
          metadataMap.set(row.file_path, row.is_featured);
        });
      }

      const mapped = data
        .filter(obj => !obj.name.startsWith('.'))
        .map((obj) => {
          const filePath = `user_directory/${trainerAuthUid}/${obj.name}`;
          const { data: { publicUrl } } = supabase.storage
            .from('trainer_media')
            .getPublicUrl(filePath);

          const isVideo = /\.(mp4|mov|webm|avi)$/i.test(obj.name);

          return {
            id: obj.name.split('.')[0],
            name: obj.name,
            path: filePath,
            publicUrl,
            createdAt: obj.created_at ?? new Date().toISOString(),
            type: isVideo ? 'video' : 'image',
            isFeatured: metadataMap.get(filePath) || false
          } as TrainerMedia;
        });

      setFiles(mapped);
    } catch (err) {
      console.error('Failed to list trainer media:', err);
      setError(err instanceof Error ? err.message : 'Failed to load media');
    } finally {
      setLoading(false);
    }
  }, [trainerAuthUid]);

  /* -------------------------------------------------- */
  /* Upload                                             */
  /* -------------------------------------------------- */
  const upload = useCallback(
    async (fileList: FileList) => {
      if (!trainerAuthUid) return;
      setError(null);

      try {
        const uploads = [...fileList].map(async (file) => {
          if (!file.type.startsWith('image/') && !file.type.startsWith('video/')) {
            throw new Error('Only images and videos are allowed');
          }
          if (file.size > 500 * 1024 * 1024) {
            throw new Error('File size must be less than 500 MB');
          }

          const timestamp = Date.now();
          const safeFileName = file.name.replace(/[^a-z0-9.]/gi, '_').toLowerCase();
          const filePath = `user_directory/${trainerAuthUid}/${timestamp}-${safeFileName}`;

          // ← Console log the exact path sent to Supabase:
          console.log('trainer_media upload path:', filePath);

          const { error } = await supabase.storage
            .from('trainer_media')
            .upload(filePath, file, {
              upsert: false,
              contentType: file.type,
              cacheControl: '3600'
            });

          if (error) throw error;
        });

        await Promise.all(uploads);
        await refresh();
      } catch (err) {
        console.error('Error uploading files:', err);
        setError(err instanceof Error ? err.message : 'Failed to upload files');
        throw err;
      }
    },
    [trainerAuthUid, refresh]
  );

  /* -------------------------------------------------- */
  /* Remove                                             */
  /* -------------------------------------------------- */
  const remove = useCallback(
    async (filePath: string) => {
      if (!trainerAuthUid) return;
      setError(null);

      try {
        // Optimistically update UI by removing the file from state
        const fileToRemove = filesRef.current.find(f => f.path === filePath);
        if (fileToRemove) {
          setFiles(prev => prev.filter(f => f.path !== filePath));
        }

        const { error } = await supabase.storage
          .from('trainer_media')
          .remove([filePath]);

        if (error) {
          // If there was an error, revert the optimistic update
          await refresh();
          throw error;
        }
        
        // Also remove any metadata for this file
        await supabase
          .from('trainer_media_metadata')
          .delete()
          .eq('file_path', filePath);
        
        // No need to refresh since we've already updated the UI
        return true;
      } catch (err) {
        console.error('Error deleting file:', err);
        setError(err instanceof Error ? err.message : 'Failed to delete file');
        throw err;
      }
    },
    [trainerAuthUid, refresh, filesRef]
  );

  /* -------------------------------------------------- */
  /* Toggle Featured Status                             */
  /* -------------------------------------------------- */
  const toggleFeatured = useCallback(
    async (filePath: string, isFeatured: boolean) => {
      if (!trainerAuthUid) return;
      setError(null);

      try {
        // Get current user's database ID
        const { data: { user }, error: userError } = await supabase.auth.getUser();
        if (userError) throw userError;
        if (!user) throw new Error('Not authenticated');

        const { data: userData, error: userDataError } = await supabase
          .from('users')
          .select('id')
          .eq('auth_id', user.id)
          .single();

        if (userDataError) throw userDataError;
        if (!userData) throw new Error('User not found');

        // Count existing featured items if we're trying to feature a new item
        if (isFeatured) {
          const { count, error: countError } = await supabase
            .from('trainer_media_metadata')
            .select('*', { count: 'exact', head: true })
            .eq('user_id', userData.id)
            .eq('is_featured', true);

          if (countError) throw countError;
          
          // Check if we already have 6 featured items
          if (count && count >= 6) {
            throw new Error('You can only feature up to 6 items');
          }
        }

        // Upsert metadata record
        const { error: upsertError } = await supabase
          .from('trainer_media_metadata')
          .upsert({
            file_path: filePath,
            user_id: userData.id,
            is_featured: isFeatured
          }, {
            onConflict: 'file_path'
          });

        if (upsertError) throw upsertError;

        // Update local state
        setFiles(prev => prev.map(file => {
          if (file.path === filePath) {
            return { ...file, isFeatured };
          }
          return file;
        }));

        return true;
      } catch (err) {
        console.error('Error toggling featured status:', err);
        setError(err instanceof Error ? err.message : 'Failed to update featured status');
        throw err;
      }
    },
    [trainerAuthUid]
  );

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { files, loading, error, refresh, upload, remove, toggleFeatured };
}
